import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import PrdArtifactCard from '@/components/PrdArtifactCard';
import { displayChatMessageContent, isHiddenChatMessage } from '@/lib/chatDisplay';
import { useChatPrdAvailable, shouldShowPrdArtifact } from '@/hooks/useChatPrdAvailable';
import {
  formatApiError,
  loadChatSessionThread,
  mapChatResponse,
  normalizeSessionsPayload,
  stripCodeFences,
  sessionIdOf,
  phaseOf,
  previewOf,
  dateOf,
} from '@/lib/vibeChatApi';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  ArrowLeft,
  Brain,
  Sparkles,
  Code2,
  Loader2,
  RefreshCw,
  MessageSquarePlus,
  PanelLeft,
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  Activity,
} from 'lucide-react';

const SESSIONS_LIMIT = 50;

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-0.5" aria-hidden>
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="h-2 w-2 rounded-full bg-purple-500 opacity-70"
          style={{ animation: 'vibeWsDot 1.4s infinite ease-in-out', animationDelay: `${delay}ms` }}
        />
      ))}
      <style>{`
        @keyframes vibeWsDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function ThreadBubble({ role, content, index, displayContent }) {
  const text = displayContent ?? content;
  const isUser = role === 'user';
  return (
    <div
      className={cn(
        'vibe-ws-msg flex min-w-0 gap-3 opacity-0',
        isUser && 'flex-row-reverse'
      )}
      style={{
        animation: 'vibeWsMsgIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        animationDelay: `${Math.min(index, 20) * 40}ms`,
      }}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-lg ring-2 ring-offset-2 ring-offset-white/80',
          isUser
            ? 'bg-gradient-to-br from-blue-500 to-indigo-600 ring-blue-200/80'
            : 'bg-gradient-to-br from-purple-500 to-indigo-600 ring-purple-200/80'
        )}
      >
        {isUser ? 'You' : <Brain className="h-4 w-4" />}
      </div>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-2xl border px-4 py-3 shadow-md transition-transform duration-300 hover:scale-[1.01]',
          isUser
            ? 'border-blue-200/70 bg-gradient-to-br from-blue-50 to-indigo-50/80'
            : 'border-purple-100/90 bg-white/95 backdrop-blur-md'
        )}
      >
        <div
          className={cn(
            'mb-1 text-[10px] font-bold uppercase tracking-widest',
            isUser ? 'text-blue-700' : 'text-purple-700'
          )}
        >
          {isUser ? 'You' : 'Assistant'}
        </div>
        <div
          className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-800"
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function usePhaseVisual(phase, chatFinished, chatAwaitingUser, hasSession) {
  return useMemo(() => {
    const p = (phase || '').toUpperCase();
    if (chatFinished || p === 'DONE') {
      return {
        progress: 100,
        headline: 'Complete',
        sub: 'ABAP ready when generated.',
        badgeClass: 'border-emerald-300 bg-emerald-50 text-emerald-900',
        bar: 'from-emerald-500 to-teal-500',
      };
    }
    if (p === 'CONFIRM') {
      return {
        progress: 72,
        headline: 'Confirm',
        sub: 'Open the PRD link below, then approve with yes / ok / approve.',
        badgeClass: 'border-amber-300 bg-amber-50 text-amber-900',
        bar: 'from-amber-500 to-orange-500',
      };
    }
    if (p === 'GATHER') {
      return {
        progress: 38,
        headline: 'Gathering',
        sub: 'Clarifying your requirements.',
        badgeClass: 'border-violet-300 bg-violet-50 text-violet-900',
        bar: 'from-violet-500 to-indigo-600',
      };
    }
    if (hasSession && chatAwaitingUser) {
      return {
        progress: 55,
        headline: 'Your turn',
        sub: 'Reply below.',
        badgeClass: 'border-blue-300 bg-blue-50 text-blue-900',
        bar: 'from-blue-500 to-indigo-600',
      };
    }
    if (hasSession) {
      return {
        progress: 28,
        headline: 'Running',
        sub: 'Follow the thread.',
        badgeClass: 'border-purple-200 bg-purple-50 text-purple-900',
        bar: 'from-purple-500 to-indigo-600',
      };
    }
    return {
      progress: 0,
      headline: 'Idle',
      sub: 'Pick a session or start new.',
      badgeClass: 'border-slate-200 bg-slate-50 text-slate-700',
      bar: 'from-slate-400 to-slate-600',
    };
  }, [phase, chatFinished, chatAwaitingUser, hasSession]);
}

export default function VibeChatWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSessionId = searchParams.get('session');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const [chatAwaitingUser, setChatAwaitingUser] = useState(false);
  const [chatPhase, setChatPhase] = useState('');
  const [chatGeneratedCode, setChatGeneratedCode] = useState(null);

  const [chatGenerating, setChatGenerating] = useState(false);
  const [prdAvailableHint, setPrdAvailableHint] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const threadEndRef = useRef(null);
  const activeThreadRef = useRef(null);
  // Mirror selectedId into a ref so effects and callbacks can read the current
  // value without taking it as a dependency (prevents URL-sync loop).
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  // Track which session IDs have already played their entry animation so that
  // deselecting a session does not re-trigger the fade-from-zero animation.
  const animatedSessionsRef = useRef(new Set());

  const { prdAvailable, prdChecking, markPrdAvailable } = useChatPrdAvailable(
    selectedId,
    prdAvailableHint
  );

  const applyPrdFlag = (m) => {
    if (m?.prdAvailable) {
      setPrdAvailableHint(true);
      markPrdAvailable();
    }
  };

  const chatFinished = !chatAwaitingUser && String(chatPhase).toUpperCase() === 'DONE';

  const phaseVisual = usePhaseVisual(chatPhase, chatFinished, chatAwaitingUser, Boolean(selectedId));

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatGenerating, selectedId]);

  const loadSessions = useCallback(async () => {
    if (!VIBE_CHAT_API_BASE) {
      setSessionsLoading(false);
      setSessionsError('Chat API URL not configured.');
      return;
    }
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/sessions`, {
        params: { limit: SESSIONS_LIMIT, offset: 0 },
      });
      const { items } = normalizeSessionsPayload(data);
      setSessions(items);
    } catch (err) {
      setSessionsError(formatApiError(err));
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const applyMeta = useCallback((meta) => {
    if (meta?.phase != null && String(meta.phase) !== '') {
      setChatPhase(String(meta.phase).toUpperCase());
    }
    const au = meta?.awaiting_user ?? meta?.awaitingUser;
    if (au !== undefined && au !== null) {
      setChatAwaitingUser(Boolean(au));
    }
    const gc = meta?.generated_code ?? meta?.generatedCode;
    if (gc != null && String(gc).trim() !== '') {
      setChatGeneratedCode(stripCodeFences(String(gc)));
    }
    const pa = meta?.prd_available ?? meta?.prdAvailable;
    if (pa !== undefined && pa !== null) {
      setPrdAvailableHint(Boolean(pa));
      if (pa) {
        markPrdAvailable();
      }
    }
  }, [markPrdAvailable]);

  const fetchThread = useCallback(
    async (sessionId, seedM = null) => {
      if (!VIBE_CHAT_API_BASE || !sessionId) return;
      activeThreadRef.current = sessionId;
      setThreadLoading(true);
      setThreadError(null);
      try {
        const { messages: list, meta } = await loadChatSessionThread(sessionId, VIBE_CHAT_API_BASE);
        if (activeThreadRef.current !== sessionId) return;
        let finalList = list;
        if (finalList.length === 0 && seedM?.message) {
          finalList = [{ role: 'assistant', content: seedM.message }];
        }
        setMessages(finalList);
        applyMeta({
          ...meta,
          phase: meta.phase || seedM?.phase,
          awaiting_user:
            meta.awaiting_user !== undefined && meta.awaiting_user !== null
              ? meta.awaiting_user
              : seedM?.awaitingUser,
          generated_code: meta.generated_code ?? seedM?.generatedCode,
          prd_available: meta.prd_available ?? seedM?.prdAvailable,
        });
      } catch (err) {
        if (activeThreadRef.current !== sessionId) return;
        setThreadError(formatApiError(err));
        setMessages([]);
      } finally {
        if (activeThreadRef.current === sessionId) {
          setThreadLoading(false);
        }
      }
    },
    [applyMeta]
  );

  const selectSession = useCallback(
    async (id, seedM = null) => {
      if (!id || id === selectedIdRef.current) return;
      // Update ref immediately so re-entrant calls (URL-sync effect) are blocked.
      selectedIdRef.current = id;
      activeThreadRef.current = id;
      setSelectedId(id);
      setChatPhase('');
      setChatAwaitingUser(false);
      setChatGeneratedCode(null);
      setPrdAvailableHint(false);
      setChatGenerating(false);
      setActionError(null);
      setMessages([]);
      await fetchThread(id, seedM);
    },
    [fetchThread]
  );

  // Sync selectedId → URL (one-way; keeps URL in step with sidebar clicks).
  useEffect(() => {
    if (selectedId) setSearchParams({ session: selectedId }, { replace: true });
  }, [selectedId, setSearchParams]);

  useEffect(() => {
    if (!VIBE_CHAT_API_BASE || !selectedId || !chatGenerating) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/${selectedId}/status`);
        const m = mapChatResponse(data);
        if (cancelled) return;
        applyPrdFlag(m);
        setChatPhase(m.phase);
        setChatAwaitingUser(m.awaitingUser);
        if (m.generatedCode) {
          setChatGeneratedCode(m.generatedCode);
        }
        if (!m.generating) {
          setChatGenerating(false);
          await fetchThread(selectedId, m);
          await loadSessions();
        }
      } catch (err) {
        if (!cancelled) {
          setActionError(formatApiError(err));
          setChatGenerating(false);
        }
      }
    };
    poll();
    const timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedId, chatGenerating, fetchThread, loadSessions]);

  // Load session from URL on initial mount / external navigation.
  // Intentionally omits selectSession from deps — selectSession is stable
  // (depends only on fetchThread) so this is safe, and including it would
  // re-run this effect on every selectedId change, causing a reset loop.
  useEffect(() => {
    if (!urlSessionId || !VIBE_CHAT_API_BASE || sessionsLoading) return;
    if (selectedIdRef.current === urlSessionId) return;
    void selectSession(urlSessionId, null);
    // eslint-disable-next-line -- selectSession is intentionally excluded to avoid reset loop
  }, [urlSessionId, sessionsLoading]);

  const copyCode = async () => {
    const code = chatGeneratedCode || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Could not copy');
    }
  };

  const showCodePanel = (chatGeneratedCode != null && chatGeneratedCode !== '') || chatFinished;
  const showPrdArtifact =
    Boolean(selectedId) && shouldShowPrdArtifact(chatPhase, prdAvailable || prdAvailableHint);

  return (
    <div
      className="vibe-chat-workspace -mx-6 -mt-2 flex min-h-[calc(100vh-6.5rem)] flex-col gap-0 pb-8 sm:-mx-6"
      data-testid="vibe-chat-workspace"
    >
      <style>{`
        @keyframes vibeWsShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes vibeWsMsgIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .vibe-ws-hero-shimmer {
          background: linear-gradient(
            110deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.55) 45%,
            rgba(255,255,255,0) 90%
          );
          background-size: 200% 100%;
          animation: vibeWsShimmer 4s ease-in-out infinite;
        }
        @keyframes vibeWsGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.25); }
          50% { box-shadow: 0 0 24px 2px rgba(139, 92, 246, 0.2); }
        }
        .vibe-ws-selected {
          animation: vibeWsGlow 2.5s ease-in-out infinite;
        }
      `}</style>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-purple-100/80 bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 px-4 py-5 text-white sm:px-6">
        <div className="pointer-events-none absolute inset-0 vibe-ws-hero-shimmer opacity-30" aria-hidden />
        <div className="pointer-events-none absolute -left-20 top-0 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -right-10 bottom-0 h-48 w-48 rounded-full bg-indigo-400/20 blur-3xl" aria-hidden />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              className="border-0 bg-white/10 text-white hover:bg-white/20"
              asChild
            >
              <Link to="/vibe-studio">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Studio
              </Link>
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="border-0 bg-white/10 text-white hover:bg-white/20 lg:hidden"
              onClick={() => setSidebarOpen((o) => !o)}
            >
              <PanelLeft className="mr-2 h-4 w-4" />
              Sessions
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur-sm">
              <Sparkles className="h-6 w-6 text-amber-200" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Chat history</h1>
              <p className="text-xs text-indigo-200/90 sm:text-sm">
                Browse past sessions and open threads — start a new program in Vibe Coding Studio
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={loadSessions}
              disabled={sessionsLoading || !VIBE_CHAT_API_BASE}
              variant="secondary"
              className="border-0 bg-white/15 text-white hover:bg-white/25"
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', sessionsLoading && 'animate-spin')} />
              Refresh list
            </Button>
            <Button
              type="button"
              size="sm"
              asChild
              className="bg-gradient-to-r from-amber-400 to-orange-500 font-semibold text-slate-900 shadow-lg hover:from-amber-300 hover:to-orange-400"
            >
              <Link to="/vibe-studio" data-testid="chat-history-new-chat">
                <MessageSquarePlus className="mr-2 h-4 w-4" />
                New chat
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {!VIBE_CHAT_API_BASE && (
        <div className="px-4 pt-4 sm:px-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Chat unavailable</AlertTitle>
            <AlertDescription>
              Chat history is not available in this environment. Contact your administrator if this persists.
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex flex-1 min-h-0 flex-col gap-0 lg:flex-row">
        {/* Sessions sidebar */}
        <aside
          className={cn(
            'flex w-full flex-col border-b border-purple-100/80 bg-gradient-to-b from-white via-purple-50/30 to-indigo-50/40 transition-all duration-500 ease-out lg:w-[min(100%,20rem)] lg:border-b-0 lg:border-r',
            !sidebarOpen && 'hidden lg:flex'
          )}
        >
          <div className="flex items-center justify-between border-b border-purple-100/60 bg-gradient-to-r from-purple-100/40 to-transparent px-3 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-purple-950">
              <Activity className="h-4 w-4 text-purple-600" />
              Sessions
            </div>
            <Badge variant="outline" className="border-purple-200 bg-white/80 text-[10px]">
              {sessions.length} sessions
            </Badge>
          </div>
          <ScrollArea className="h-[min(40vh,320px)] flex-1 lg:h-auto lg:min-h-[calc(100vh-14rem)]">
            <div className="space-y-1.5 p-2">
              {sessionsLoading && (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-purple-800/80">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                  <span className="text-xs">Loading…</span>
                </div>
              )}
              {sessionsError && !sessionsLoading && (
                <Alert variant="destructive" className="text-xs">
                  <AlertDescription className="font-mono">{sessionsError}</AlertDescription>
                </Alert>
              )}
              {!sessionsLoading &&
                !sessionsError &&
                sessions.map((row, idx) => {
                  const id = sessionIdOf(row);
                  const active = id && id === selectedId;
                  const ph = phaseOf(row);
                  const dt = dateOf(row);
                  // Only animate on first mount; skip re-animation when deselected.
                  const hasAnimated = animatedSessionsRef.current.has(id);
                  if (id && !hasAnimated) animatedSessionsRef.current.add(id);
                  return (
                    <button
                      key={id || `s-${idx}`}
                      type="button"
                      onClick={() => id && void selectSession(id, null)}
                      disabled={!id}
                      className={cn(
                        'group w-full rounded-xl border px-3 py-2.5 text-left transition-all duration-300',
                        active
                          ? 'vibe-ws-selected border-purple-400 bg-gradient-to-br from-white to-purple-50 shadow-md ring-2 ring-purple-300/50 !opacity-100'
                          : 'border-transparent bg-white/40 opacity-100 hover:border-purple-200/80 hover:bg-white/90 hover:shadow-sm'
                      )}
                      style={
                        active || hasAnimated
                          ? undefined
                          : {
                              animation: 'vibeWsMsgIn 0.4s ease both',
                              animationDelay: `${Math.min(idx, 12) * 35}ms`,
                            }
                      }
                      aria-current={active ? 'true' : undefined}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate font-mono text-[10px] text-slate-600">{id || '—'}</span>
                        {ph && (
                          <Badge variant="outline" className="h-5 shrink-0 border-purple-200 px-1.5 text-[9px]">
                            {ph}
                          </Badge>
                        )}
                      </div>
                      {dt && <div className="mt-0.5 text-[10px] text-muted-foreground">{dt.toLocaleString()}</div>}
                      <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-600">{previewOf(row) || '—'}</p>
                    </button>
                  );
                })}
              {!sessionsLoading && !sessionsError && sessions.length === 0 && (
                <p className="px-2 py-8 text-center text-xs text-muted-foreground">No sessions yet. Start a new chat.</p>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Main thread */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col bg-gradient-to-br from-slate-50/80 via-white to-indigo-50/20">
          <div className="border-b border-slate-200/80 bg-white/70 px-4 py-3 backdrop-blur-md sm:px-5">
            <div className="flex flex-wrap items-center gap-2">
              {chatPhase && (
                <Badge className={cn('text-xs font-medium', phaseVisual.badgeClass)} variant="outline">
                  {phaseVisual.headline}
                </Badge>
              )}
              {selectedId &&
                (chatAwaitingUser ? (
                  <Badge className="border-blue-300 bg-blue-50 text-[10px] text-blue-900" variant="outline">
                    Your turn
                  </Badge>
                ) : !chatFinished ? (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    In progress
                  </Badge>
                ) : null)}
            </div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>{phaseVisual.headline}</span>
                <span>{phaseVisual.progress}%</span>
              </div>
              <div className="relative h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div
                  className={cn(
                    'h-full rounded-full bg-gradient-to-r transition-all duration-700 ease-out',
                    phaseVisual.bar
                  )}
                  style={{ width: `${phaseVisual.progress}%` }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">{phaseVisual.sub}</p>
            </div>
          </div>

          {threadError && (
            <div className="px-4 pt-3 sm:px-5">
              <Alert variant="destructive">
                <AlertTitle className="text-sm">Thread</AlertTitle>
                <AlertDescription className="font-mono text-xs">{threadError}</AlertDescription>
              </Alert>
            </div>
          )}
          {actionError && (
            <div className="px-4 pt-3 sm:px-5">
              <Alert variant="destructive">
                <AlertTitle className="text-sm">Action</AlertTitle>
                <AlertDescription className="font-mono text-xs">{actionError}</AlertDescription>
              </Alert>
            </div>
          )}

          <ScrollArea className="min-h-[280px] flex-1">
            <div className="space-y-4 p-4 sm:p-5">
              {!selectedId && (
                <div className="mx-auto mt-8 max-w-md rounded-2xl border border-dashed border-purple-200 bg-gradient-to-br from-purple-50/80 to-indigo-50/50 p-10 text-center shadow-inner">
                  <Brain className="mx-auto mb-4 h-14 w-14 text-purple-300 drop-shadow-md" />
                  <p className="font-semibold text-slate-800">Select a session</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Choose a session from the list, or start a <strong>New chat</strong> in Vibe Coding Studio.
                  </p>
                </div>
              )}
              {selectedId && threadLoading && (
                <div className="flex flex-col items-center justify-center gap-3 py-20">
                  <Loader2 className="h-10 w-10 animate-spin text-purple-500" />
                  <span className="text-sm text-purple-900/80">Loading thread…</span>
                </div>
              )}
              {selectedId && !threadLoading && messages.filter((m) => !isHiddenChatMessage(m)).map((msg, i) => (
                <ThreadBubble
                  key={`${selectedId}-${i}-${msg.role}`}
                  role={msg.role}
                  content={msg.content}
                  index={i}
                  displayContent={displayChatMessageContent(msg.content, {
                    role: msg.role,
                    generatedCode: msg.generatedCode ?? chatGeneratedCode,
                    phase: msg.phase ?? chatPhase,
                  })}
                />
              ))}
              {chatGenerating && (
                <div className="flex gap-3 pl-1">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg ring-2 ring-purple-200">
                    <Brain className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 shadow-md">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-purple-900">Assistant</span>
                      <TypingDots />
                    </div>
                    <p className="text-xs text-purple-700">Working on your program…</p>
                  </div>
                </div>
              )}
              <div ref={threadEndRef} />
            </div>
          </ScrollArea>

          <div className="border-t border-purple-100 bg-purple-50/40 px-4 py-3 text-center text-xs text-purple-900/90 sm:px-5">
            {selectedId ? (
              <>
                Read-only view of a past conversation. To send messages, start a{' '}
                <Link to="/vibe-studio" className="font-semibold text-purple-800 underline-offset-2 hover:underline">
                  new chat in Vibe Coding Studio
                </Link>
                .
              </>
            ) : (
              <>Select a session from the list, or start a new chat in Vibe Coding Studio.</>
            )}
          </div>
        </main>

        {/* Artifacts rail */}
        <aside className="hidden w-[min(100%,22rem)] shrink-0 flex-col gap-3 overflow-y-auto border-l border-slate-200/80 bg-white/90 p-4 backdrop-blur-md xl:flex">
          {showPrdArtifact && (
            <PrdArtifactCard
              key={selectedId}
              sessionId={selectedId}
              phase={chatPhase}
              prdAvailable={prdAvailable}
              prdChecking={prdChecking}
            />
          )}
          <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <Code2 className="h-5 w-5 text-slate-700" />
              <span className="font-semibold text-slate-900">Generated ABAP</span>
            </div>
            <p className="text-[11px] text-muted-foreground">Program produced for this session</p>
          </div>
          <div className="flex flex-col gap-3 p-4">
            {showCodePanel ? (
              <>
                <div className="flex justify-end">
                  <Button type="button" variant="outline" size="sm" onClick={copyCode} disabled={!chatGeneratedCode}>
                    {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                    Copy
                  </Button>
                </div>
                <div className="max-h-[min(60vh,480px)] flex-1 overflow-auto rounded-xl border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-100 shadow-inner ring-1 ring-purple-900/30">
                  <pre className="whitespace-pre-wrap break-words">{chatGeneratedCode || '—'}</pre>
                </div>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-xs text-muted-foreground">
                No ABAP generated in this session
              </div>
            )}
          </div>
          </div>
        </aside>
      </div>

      {/* Mobile artifacts */}
      <div className="space-y-4 border-t border-slate-200 bg-slate-50 p-4 xl:hidden">
        {showPrdArtifact && (
          <PrdArtifactCard
            key={selectedId}
            sessionId={selectedId}
            phase={chatPhase}
            prdAvailable={prdAvailable}
            prdChecking={prdChecking}
          />
        )}
        <div className="text-xs font-semibold text-slate-800">Generated ABAP</div>
        {showCodePanel ? (
          <pre className="mt-2 max-h-40 overflow-auto rounded-lg border bg-slate-950 p-2 font-mono text-[10px] text-slate-100">
            {chatGeneratedCode || '—'}
          </pre>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">—</p>
        )}
      </div>
    </div>
  );
}
